/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import { useMemo } from "react";
import * as React from "react";
import enhanceThumbnailBig1x from "images/home/landing/toolsthumbnails/enhance-thumbnail-big@1x.jpg";
import enhanceThumbnailBig2x from "images/home/landing/toolsthumbnails/enhance-thumbnail-big@2x.jpg";
import micCheckThumbnailBig1x from "images/home/landing/toolsthumbnails/mic-check-thumbnail-big@1x.jpg";
import micCheckThumbnailBig2x from "images/home/landing/toolsthumbnails/mic-check-thumbnail-big@2x.jpg";
import studioThumbnailBig1x from "images/home/landing/toolsthumbnails/studio-thumbnail-big@1x.jpg";
import studioThumbnailBig2x from "images/home/landing/toolsthumbnails/studio-thumbnail-big@2x.jpg";
import {
  devices,
  fontFamilies,
  isMobileSize,
  useScreenSize,
} from "components/shared/Styles";
import { useHistory } from "react-router-dom";
import {
  BetaBadge,
  QuickToolType,
  QuickToolsSize,
} from "components/shared/QuickTools";
import { useNavigateToStudio } from "utilities/AuthenticatedNavigation";

export const ToolsLineup: React.FC = () => {
  return (
    <ToolsLineupContainer>
      <Tools
        toolsToShow={[
          QuickToolType.EnhanceSpeech,
          QuickToolType.Studio,
          QuickToolType.MicCheck,
        ]}
      />
    </ToolsLineupContainer>
  );
};

export const Tools: React.FC<{
  toolsToShow: QuickToolType[];
  imageAspect?: number;
}> = ({ toolsToShow, imageAspect = 1.25 }) => {
  const history = useHistory();
  const { navigateToStudio } = useNavigateToStudio();
  const badgeSize = useMemo(() => {
    if (isMobileSize()) {
      return QuickToolsSize.Small;
    }

    return QuickToolsSize.Large;
  }, []);

  const availableTools = {
    [QuickToolType.EnhanceSpeech]: (
      <ToolsLineupItem
        key={QuickToolType.EnhanceSpeech}
        imageAspect={imageAspect}
        thumbnail1x={enhanceThumbnailBig1x}
        thumbnail2x={enhanceThumbnailBig2x}
        title={I18n.t("landing.tools_lineup.enhance.title")}
        subtitle={I18n.t("landing.tools_lineup.enhance.subtitle")}
        action={I18n.t("landing.tools_lineup.enhance.action")}
        onClick={(): void => history.push("/enhance")}
      />
    ),
    [QuickToolType.MicCheck]: (
      <ToolsLineupItem
        key={QuickToolType.MicCheck}
        imageAspect={imageAspect}
        thumbnail1x={micCheckThumbnailBig1x}
        thumbnail2x={micCheckThumbnailBig2x}
        title={I18n.t("landing.tools_lineup.mic_check.title")}
        subtitle={I18n.t("landing.tools_lineup.mic_check.subtitle")}
        action={I18n.t("landing.tools_lineup.mic_check.action")}
        onClick={(): void => history.push("/miccheck")}
      />
    ),
    [QuickToolType.Studio]: (
      <ToolsLineupItem
        key={QuickToolType.Studio}
        imageAspect={imageAspect}
        thumbnail1x={studioThumbnailBig1x}
        thumbnail2x={studioThumbnailBig2x}
        title={I18n.t("landing.tools_lineup.studio.title")}
        subtitle={I18n.t("landing.tools_lineup.studio.subtitle")}
        action={I18n.t("landing.tools_lineup.studio.action.go_to_studio")}
        onClick={navigateToStudio}
        titleBadge={<BetaBadge />}
      />
    ),
  };
  return <>{toolsToShow.map((tool) => availableTools[tool])}</>;
};

interface ToolsLineupItemProps {
  thumbnail1x: string;
  thumbnail2x: string;
  title: string;
  subtitle: string;
  action: string;
  onClick: VoidFunction;
  imageAspect: number;
  badge?: React.ReactNode;
  titleBadge?: React.ReactNode;
}

const ToolsLineupItem: React.FC<ToolsLineupItemProps> = (props) => {
  const screenSize = useScreenSize();
  const toolButtonSize = useMemo(() => {
    if (isMobileSize()) {
      return "xl";
    }

    if (screenSize > 1000) {
      return "xl";
    }

    return "m";
  }, [screenSize]);

  return (
    <ToolSectionContainer onClick={props.onClick}>
      <TextContainer>
        <ImageContainer>
          <ToolImage
            imageAspect={props.imageAspect}
            srcSet={`${props.thumbnail1x} 1x, ${props.thumbnail2x} 2x`}
            alt=""
          />
          {props.badge}
        </ImageContainer>
        <ToolTitle>
          {props.title}
          {props.titleBadge}
        </ToolTitle>
        <ToolSubtitle>{props.subtitle}</ToolSubtitle>
      </TextContainer>
      <sp-button
        href="#"
        treatment="fill"
        variant="primary"
        size={toolButtonSize}
      >
        <ButtonLabelText>{props.action}</ButtonLabelText>
      </sp-button>
    </ToolSectionContainer>
  );
};

// This is a workaround to add overflow wrap break words behavior to the label text content of the sp-button
const ButtonLabelText = styled.div`
  white-space: normal;
`;

const ImageContainer = styled.div`
  position: relative;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const ToolImage = styled.img<{ imageAspect: number }>`
  border-radius: 32px;
  width: 100%;
  margin-bottom: 16px;
  object-fit: cover;
  aspect-ratio: ${({ imageAspect }): number => imageAspect};

  ${devices.small} {
    max-height: 216px;
    border-radius: 24px;
  }
`;

const ToolTitle = styled.span`
  display: flex;
  flex-direction: row;
  font: normal normal 800 22px/27px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  gap: 8px;
  margin-bottom: 8px;
`;

const ToolSubtitle = styled.span`
  font: normal normal normal 18px/22px ${fontFamilies};
  color: var(--spectrum-global-color-gray-900);
  margin-bottom: 16px;
`;

const ToolSectionContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: space-between;
  cursor: pointer;
`;

const ToolsLineupContainer = styled.div`
  margin-top: -104px;
  margin-left: -48px;
  margin-right: -48px;
  position: relative;
  z-index: 2;
  background: white;
  box-shadow: var(--phonos-box-shadow);
  border-radius: 64px;
  padding: 48px;
  gap: 48px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  box-sizing: border-box;

  ${devices.small} {
    margin-left: 24px;
    margin-right: 24px;
    border-radius: 48px;
    flex-direction: column;
    padding: 24px;
  }
`;
